/**
 * Per-environment addressing (ADR-024 launch posture: planlets trusts the tilgong platform
 * issuer). The web page is served from a known set of hostnames (planlets.com + www and the
 * prod SWA default, dev.planlets.com + the dev SWA default, localhost during development), and
 * each maps to its environment's OpenID Provider; the native shells pick their environment at
 * build time instead (ADR-029 §4 — inside a shell the hostname is `localhost`). The client id
 * matches the auth server's registered planlets client (`extra_spa_clients` in its tfvars).
 */
export interface AuthConfig {
  /** The OpenID Provider's issuer URL (no trailing slash). */
  readonly issuer: string;
  /** The SPA's registered public client id. */
  readonly clientId: string;
}

/** The deployed environments an install can target. */
export type PlanletsEnv = 'dev' | 'prod';

const CLIENT_ID = 'planlets-spa';

/** The prod hostnames: the custom domain (apex + www) and the SWA default. */
const PROD_HOSTNAMES = new Set([
  'thankful-bay-04aff8303.7.azurestaticapps.net',
  'planlets.com',
  'www.planlets.com',
]);

/** The https hosts whose links the native apps claim and open in place (ADR-029 §6). */
export const LINK_HOSTS: readonly string[] = ['planlets.com', 'www.planlets.com', 'dev.planlets.com'];

/** Whether `hostname` is one of the production hostnames (the custom domain or its SWA). */
export function isProductionHostname(hostname: string): boolean {
  return PROD_HOSTNAMES.has(hostname);
}

/** The issuer an environment's installs sign in against. */
export function issuerFor(env: PlanletsEnv): string {
  return env === 'prod' ? 'https://auth.tilgong.com' : 'https://auth.dev.tilgong.com';
}

/** The site an environment's web page lives on — where its share links point (ADR-027 §6). */
export function siteOriginFor(env: PlanletsEnv): string {
  return env === 'prod' ? 'https://planlets.com' : 'https://dev.planlets.com';
}

/** The API base the native shells call — the SWA hostname, never the Function App host. */
export function apiBaseFor(env: PlanletsEnv): string {
  return `${siteOriginFor(env)}/api`;
}

export function authConfigFor(hostname: string): AuthConfig {
  const issuer = isProductionHostname(hostname)
    ? issuerFor('prod')
    : hostname === 'localhost' || hostname === '127.0.0.1'
      ? 'http://localhost:7071'
      : issuerFor('dev');
  return { issuer, clientId: CLIENT_ID };
}
